/**
 * App Component - Entry Point
 *
 * Main application component with auth state management and routing
 * Handles session persistence, token refresh, and user data loading
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { router } from './router';
import { supabase } from '@/services/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import { userService } from '@/services/user/userService';
import { FullPageSpinner } from '@/components/common/LoadingSpinner';
import { NetworkBanner } from '@/components/common/NetworkBanner';
import { ChunkErrorBoundary } from '@/components/common/ChunkErrorBoundary';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useDarkMode } from '@/hooks/useDarkMode';
import type { Session } from '@supabase/supabase-js';

// Timeout for profile loading (5 seconds)
const PROFILE_LOAD_TIMEOUT = 5000;

// Minimum fullscreen loading time (2 seconds for smooth branded experience)
const MIN_FULLSCREEN_LOADING_TIME = 2000;

// Helper: Promise with timeout that rejects
function withTimeout<T>(promise: Promise<T>, ms: number, errorMsg: string = 'TIMEOUT'): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error(errorMsg)), ms)),
  ]);
}

export function App() {
  const {
    setAuthUser,
    setSession,
    setUser,
    setLoading,
    setInitialized,
    isLoading,
    isInitialized,
  } = useAuthStore();

  const { isOnline, isReconnecting, reconnect } = useNetworkStatus();
  const initialSessionHandled = useRef(false);
  
  // Minimum loading time for fullscreen splash (prevents flickering)
  const [minLoadingTimeElapsed, setMinLoadingTimeElapsed] = useState(false);
  const loadingStartTime = useRef(Date.now());
  
  useEffect(() => {
    const elapsed = Date.now() - loadingStartTime.current;
    const remaining = MIN_FULLSCREEN_LOADING_TIME - elapsed;
    
    if (remaining <= 0) {
      setMinLoadingTimeElapsed(true);
    } else {
      const timer = setTimeout(() => {
        setMinLoadingTimeElapsed(true);
      }, remaining);
      return () => clearTimeout(timer);
    }
  }, []);

  // Initialize dark mode (010-modern-ui-2026)
  // This hook will automatically detect system preference or stored preference
  // and apply the dark class to document.documentElement
  useDarkMode();

  /**
   * Load user profile from database
   * For existing users, this should succeed quickly
   * For new users, the database trigger creates the profile automatically
   */
  const loadUserProfile = useCallback(
    async (session: Session): Promise<boolean> => {
      const userId = session.user.id;
      
      try {
        console.log('Loading user profile for:', userId);
        
        // Try to get existing user from public.users
        let user: any = null;
        try {
          user = await withTimeout(
            userService.getUser(userId),
            PROFILE_LOAD_TIMEOUT
          );
        } catch (error) {
          console.warn('Initial getUser failed or timed out:', error);
          if (error instanceof Error && error.message === 'TIMEOUT') {
            console.log('Retrying getUser due to timeout...');
            // Retry once with longer timeout
            try {
              user = await withTimeout(
                userService.getUser(userId),
                PROFILE_LOAD_TIMEOUT * 2
              );
            } catch (retryError) {
              console.error('Retry getUser also failed:', retryError);
              // If timeout persists, we can't do anything
              return false;
            }
          } else {
            // Non-timeout error (e.g. network error, but NOT 404 which returns null)
            // PGRST116 returns null, not error
            // Real errors are thrown by userService now
            console.error('Critical error getting user:', error);
            return false;
          }
        }
        
        if (user) {
          console.log('User profile loaded successfully');
          setUser(user);
          return true;
        }
        
        // User is null (PGRST116) - wait briefly for database trigger
        console.log('User not found (null), waiting for trigger...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Try again after waiting
        try {
          user = await withTimeout(
            userService.getUser(userId),
            PROFILE_LOAD_TIMEOUT
          );
        } catch (error) {
          console.error('Second getUser attempt failed:', error);
          return false;
        }
        
        if (user) {
          console.log('User found after waiting for trigger');
          setUser(user);
          return true;
        }
        
        // Still not found - try to create manually (fallback)
        console.log('User still not found, creating manually...');
        try {
          // This must succeed within timeout
          user = await withTimeout(
            userService.createUser({
              id: userId,
              name:
                session.user.user_metadata.full_name ??
                session.user.user_metadata.name ??
                session.user.email?.split('@')[0] ??
                'User',
              email: session.user.email!,
              avatarUrl: session.user.user_metadata.avatar_url,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }),
            PROFILE_LOAD_TIMEOUT * 2,
            'CREATE_TIMEOUT'
          );
          
          console.log('User created successfully');
          setUser(user);
          return true;
        } catch (createError: unknown) {
          const errorMessage = createError instanceof Error ? createError.message : String(createError);
          console.error('Create user failed:', errorMessage);
          
          if (errorMessage.includes('23505') || errorMessage.includes('duplicate key')) { // Unique violation
             console.log('User already exists (race condition), fetching one last time...');
             user = await userService.getUser(userId);
             if (user) {
               setUser(user);
               return true;
             }
          }
          
          return false;
        }
      } catch (error) {
        console.error('Fatal error in loadUserProfile:', error);
        return false;
      }
    },
    [setUser]
  );

    const ensureUserProfile = useCallback(async (session: Session) => {
      const existingUser = useAuthStore.getState().user;
      if (existingUser?.id === session.user.id && existingUser.language !== undefined) {
        return true;
      }
      return loadUserProfile(session);
    }, [loadUserProfile]);

    useEffect(() => {
    let mounted = true;

    // Helper to complete initialization safely
    const finalizeInit = () => {
      if (mounted && !initialSessionHandled.current) {
        console.log('Finalizing auth initialization');
        initialSessionHandled.current = true;
        setLoading(false);
        setInitialized(true);
      }
    };

    // 1. Explicitly check session on mount (more reliable than event listener for initial load)
    const initAuth = async () => {
      try {
        console.log('Starting explicit session check...');
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
          throw error;
        }

        if (session) {
          console.log('Session found via getSession:', session.user.id);
          setSession(session);
          setAuthUser(session.user);
          
          // Load user profile
          const success = await loadUserProfile(session);
          if (!success) {
            console.error('Failed to load profile for existing session. Signing out.');
            await supabase.auth.signOut();
          }
        } else {
          console.log('No session found via getSession');
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        finalizeInit();
      }
    };

    initAuth();

    // 2. Listen for auth changes (handling sign in, sign out, refresh after initial load)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, session?.user?.id);
      
      if (!mounted) return;

      setSession(session);
      setAuthUser(session?.user ?? null);

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        // If we already handled initial session and this is just a refresh/signin
        if (initialSessionHandled.current && session?.user) {
           await ensureUserProfile(session);
        } 
        // Note: INITIAL_SESSION is handled by initAuth above usually, 
        // but if onAuthStateChange fires first, initAuth's finalizeInit will ensure we don't double-init incorrectly.
        // However, to be safe, if we are NOT initialized yet and we get a valid session here, we should ensure profile.
        else if (!initialSessionHandled.current && session?.user) {
           // This path might race with initAuth, but loadUserProfile handles idempotent calls mostly.
           // Let's let initAuth handle the primary initialization to avoid AbortErrors from race conditions.
           console.log('Event received before initAuth completed. Letting initAuth handle it.');
        }
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        finalizeInit(); // Ensure init is done if we sign out during load
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [setAuthUser, setSession, setUser, setLoading, setInitialized, loadUserProfile]);

  // Show loading spinner while initializing (minimum 800ms for smooth animation)
  if (isLoading || !isInitialized || !minLoadingTimeElapsed) {
    return <FullPageSpinner />;
  }

  return (
    <ChunkErrorBoundary>
      <NetworkBanner
        isOnline={isOnline}
        isReconnecting={isReconnecting}
        onReconnect={reconnect}
      />
      <RouterProvider router={router} />
    </ChunkErrorBoundary>
  );
}
